/**
 * Linear-transform helpers used to align overlays with arbitrarily-transformed
 * elements. We compose the cumulative *linear* part of CSS transforms (rotate,
 * scale, transform — but not translate) up the ancestor chain. Translations
 * are absorbed into `getBoundingClientRect()` and don't affect the shape of
 * the parallelogram an overlay must match.
 */

export const isIdentityMatrix = (m: DOMMatrixReadOnly): boolean =>
  m.a === 1 && m.b === 0 && m.c === 0 && m.d === 1 && m.e === 0 && m.f === 0;

/**
 * Build the linear part of a single element's CSS transform from its computed
 * style. Combines independent properties per spec order
 * (rotate → scale → transform), then strips translation.
 *
 * Use this — not the cumulative matrix — when you need the element's own
 * pivot-drift math: ancestor transforms (e.g. canvas zoom) move both the
 * element and its CSS layout uniformly, so they cancel out and shouldn't
 * enter the formula.
 */
export const getOwnLinearMatrix = (element: HTMLElement): DOMMatrixReadOnly => {
  const style = getComputedStyle(element);
  const matrix = new DOMMatrix();

  const rotate = style.rotate;
  if (rotate && rotate !== "none") {
    matrix.multiplySelf(new DOMMatrix(`rotate(${rotate})`));
  }

  const scale = style.scale;
  if (scale && scale !== "none") {
    const parts = scale.split(/\s+/).map(Number);
    const sx = parts[0];
    const sy = parts.length > 1 ? parts[1] : sx;
    if (Number.isFinite(sx) && Number.isFinite(sy)) {
      matrix.multiplySelf(new DOMMatrix(`scale(${sx}, ${sy})`));
    }
  }

  const transform = style.transform;
  if (transform && transform !== "none") {
    matrix.multiplySelf(new DOMMatrix(transform));
  }

  matrix.e = 0;
  matrix.f = 0;
  return matrix;
};

/**
 * Walks from `element` outward, composing each ancestor's own linear matrix
 * into a cumulative transform. Stops at `container` (exclusive). Identity
 * matrices are skipped to avoid unnecessary work.
 */
export const getCumulativeLinearTransform = (
  element: HTMLElement,
  container: HTMLElement | null,
): DOMMatrixReadOnly => {
  let cumulative: DOMMatrixReadOnly = new DOMMatrix();
  let current: HTMLElement | null = element;
  while (current && current !== container) {
    const own = getOwnLinearMatrix(current);
    if (!isIdentityMatrix(own)) {
      cumulative = own.multiply(cumulative);
    }
    current = current.parentElement;
  }
  return cumulative;
};

/**
 * Apply a 2D linear transform (no translation) to the four corners of a rect
 * sized (width, height) anchored at origin (0, 0), and return the AABB min.
 * Used to position an overlay so its transformed AABB aligns with the
 * element's `getBoundingClientRect()`.
 */
export const intrinsicAabbUnderMatrix = (
  matrix: DOMMatrixReadOnly,
  width: number,
  height: number,
): { minX: number; minY: number } => {
  const corners: [number, number][] = [
    [0, 0],
    [width, 0],
    [0, height],
    [width, height],
  ];
  let minX = Infinity;
  let minY = Infinity;
  for (const [x, y] of corners) {
    const tx = matrix.a * x + matrix.c * y;
    const ty = matrix.b * x + matrix.d * y;
    if (tx < minX) minX = tx;
    if (ty < minY) minY = ty;
  }
  return { minX, minY };
};

/**
 * Magnitude of a linear matrix's column. For pure rotation+uniform-scale,
 * this equals the scale factor along that axis. Used to map a screen-space
 * delta into element-local space when the local space is axis-aligned-ish.
 */
export const getMatrixScaleX = (m: DOMMatrixReadOnly): number => Math.hypot(m.a, m.b);
export const getMatrixScaleY = (m: DOMMatrixReadOnly): number => Math.hypot(m.c, m.d);

/**
 * `transform-origin` expressed as fractions of the element's intrinsic
 * width/height. CSS default is `(0.5, 0.5)`. Browsers resolve % and keyword
 * origins to px in computed style — divide by element size to get fractions.
 */
export type TransformOriginFractions = { ox: number; oy: number };

/**
 * Reads `transform-origin` from `getComputedStyle` and returns it as fractions
 * of the given `width`/`height` (defaulting to the element's `offsetWidth`/
 * `offsetHeight`). Pass the fractional rendered size (e.g. an overlay's
 * `intrinsicRect`) when the fractions will be multiplied back by that same
 * size — the two bases then cancel exactly, so the result lands on the true
 * computed origin even when `offsetWidth` is rounded off the rendered width.
 * Falls back to centre `(0.5, 0.5)` for a zero basis.
 */
export const getTransformOriginFractions = (
  element: HTMLElement,
  width: number = element.offsetWidth,
  height: number = element.offsetHeight,
): TransformOriginFractions => {
  const raw = getComputedStyle(element).transformOrigin.trim();
  const [xToken = "50%", yToken = xToken] = raw.split(/\s+/);
  const ox = width === 0 ? 0.5 : (parseFloat(xToken) || 0) / width;
  const oy = height === 0 ? 0.5 : (parseFloat(yToken) || 0) / height;
  return { ox, oy };
};

/**
 * Project a screen-space delta onto an element's intrinsic local axes. Inverts
 * the cumulative rotation and divides out the cumulative scale, so a screen-x
 * drag on a rotated element returns the equivalent extension along the
 * element's local x edge in intrinsic (pre-transform) pixels.
 *
 * Skew is not modelled — for rotation + axis-aligned scale (the supported
 * resize UX) the projection is exact.
 */
export const projectScreenDeltaToLocal = (
  delta: { x: number; y: number },
  rotation: number,
  scaleX: number,
  scaleY: number,
): { x: number; y: number } => {
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  return {
    x: (cos * delta.x + sin * delta.y) / scaleX,
    y: (-sin * delta.x + cos * delta.y) / scaleY,
  };
};
